import { useCallback, useEffect, useRef, useState } from "react";

import { tableCallJoin } from "../../services/api.service.js";
import { markAudioContextDiagnostic } from "../../utils/audio-context-diagnostics.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { enqueueSerialized, leaveClient } from "./table-call-async.js";
import type { LocalCallPhase } from "./table-call-participant-directory.js";
import {
  assignRuntimeMeeting,
  clearRuntimeMeeting,
  clearRuntimeReconnect,
  incrementRuntimeOperationGeneration,
  isRuntimeReconnecting,
  resetRuntimeRecoveryCounters,
  setRuntimeMounted,
  setRuntimeDesiredTarget,
  setRuntimeScheduleTransition,
  startRuntimeReconnect,
} from "./table-call-runtime-operations.js";
import type {
  InitClientFunction,
  MeetingClient,
} from "./table-call.context.js";
import {
  DISCONNECTED_RECOVERY_TIMEOUT_MS,
  RESTARTABLE_ROOM_LEFT_STATES,
  ROOM_JOINED_TIMEOUT_MS,
} from "./table-call.provider.constants.js";
import type {
  AudioStateSetter,
  CurrentOperationArgs,
  TableCallRuntime,
  TableCallTarget,
} from "./table-call.types.js";

interface Args {
  target: TableCallTarget | null;
  initClient: InitClientFunction;
  runtime: TableCallRuntime;
  setAudioState: AudioStateSetter;
  prepareJoinedMeeting: (
    meeting: MeetingClient,
    target: TableCallTarget,
    generation: number
  ) => Promise<boolean | null>;
  onMeetingChanged: (meeting: MeetingClient | null) => void;
  initializeMeetingState: (meeting: MeetingClient) => void;
  resetDeviceState: () => void;
  stopDeviceApplication: () => void;
  clearMeetingState: () => void;
  resetMicrophoneState: () => void;
  tokenCacheRef: React.MutableRefObject<Map<string, string>>;
  setRtkMeeting: (
    meeting: MeetingClient | null,
    generation: number,
    lifecycleHandlers?: {
      getRoomLeftPhase?: (state: string) => LocalCallPhase;
      retainRemotesOnRoomLeft?: (state: string) => boolean;
      isCurrent?: () => boolean;
      onRoomJoined?: () => void;
      onRoomLeft?: (state: string) => void;
    }
  ) => void;
  setLocalCallPhase: (phase: LocalCallPhase, generation: number) => void;
}

const getTargetLogData = (target: TableCallTarget | null) =>
  target
    ? {
        fluidId: target.fluidId,
        tableId: target.tableId,
      }
    : null;

const getElapsedMs = (startedAt: number) =>
  Math.round(
    (typeof performance === "undefined" ? Date.now() : performance.now()) -
      startedAt
  );

const getNow = () =>
  typeof performance === "undefined" ? Date.now() : performance.now();

const logLifecycle = (
  event: string,
  {
    durationMs,
    generation,
    reason,
    target,
  }: {
    durationMs?: number;
    generation?: number;
    reason?: string;
    target?: TableCallTarget | null;
  } = {}
) => {
  console.info("[TableCall]", event, {
    durationMs,
    generation,
    reason,
    target: getTargetLogData(target ?? null),
  });
};

const toError = (error: unknown, fallbackMessage: string): Error => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(typeof error === "string" ? error : fallbackMessage);
};

const isAuthenticationError = (error: unknown) => {
  const { message } = toError(error, "RealtimeKit authentication error");
  return /auth|token|credential|unauthori[sz]ed|forbidden|expired/iu.test(
    message
  );
};

const isJoinTargetCurrent = (
  runtime: TableCallRuntime,
  target: TableCallTarget
) =>
  runtime.isMountedRef.current &&
  runtime.desiredTargetRef.current?.key === target.key;

const isJoinOperationCurrent = (
  runtime: TableCallRuntime,
  target: TableCallTarget,
  generation: number
) =>
  isJoinTargetCurrent(runtime, target) &&
  runtime.operationGenerationRef.current === generation;

const isTransientRoomLeftState = (state: string) => state === "disconnected";

const reportLifecycleError = (
  event: string,
  error: unknown,
  {
    durationMs,
    generation,
    reason,
    target,
  }: {
    durationMs?: number;
    generation?: number;
    reason?: string;
    target?: TableCallTarget | null;
  } = {}
) => {
  const reportedError = toError(error, event);
  const extra = {
    durationMs,
    generation,
    reason,
    target: getTargetLogData(target ?? null),
  };
  console.error("[TableCall]", event, extra, reportedError);
  recordErrorInSentry(reportedError, {
    extra,
    tags: { event, feature: "table-call" },
  });
};

export const useTableCallSession = ({
  target,
  initClient,
  runtime,
  setAudioState,
  prepareJoinedMeeting,
  onMeetingChanged,
  initializeMeetingState,
  resetDeviceState,
  stopDeviceApplication,
  clearMeetingState,
  resetMicrophoneState,
  tokenCacheRef,
  setRtkMeeting,
  setLocalCallPhase,
}: Args) => {
  const [activeTarget, setActiveTarget] = useState<TableCallTarget | null>(
    null
  );
  const [currentMeeting, setCurrentMeeting] = useState<MeetingClient | null>(
    null
  );
  const initClientRef = useRef(initClient);
  const previousTargetKeyRef = useRef<string | null>(target?.key ?? null);
  const roomJoinedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const disconnectedRecoveryTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  useEffect(() => {
    initClientRef.current = initClient;
  }, [initClient]);

  const clearMeeting = useCallback(() => {
    clearRuntimeMeeting(runtime);
    setCurrentMeeting(null);
    setActiveTarget(null);
    onMeetingChanged(null);
    clearMeetingState();
  }, [clearMeetingState, onMeetingChanged, runtime]);
  const assignMeeting = useCallback(
    (meeting: MeetingClient, nextTarget: TableCallTarget) => {
      assignRuntimeMeeting(runtime, meeting, nextTarget);
      setCurrentMeeting(meeting);
      setActiveTarget(nextTarget);
      onMeetingChanged(meeting);
      initializeMeetingState(meeting);
    },
    [initializeMeetingState, onMeetingChanged, runtime]
  );
  const isCurrentOperation = useCallback(
    ({
      target: operationTarget,
      targetMeeting,
      generation,
    }: CurrentOperationArgs) =>
      runtime.isMountedRef.current &&
      runtime.desiredTargetRef.current?.key === operationTarget.key &&
      runtime.meetingRef.current === targetMeeting &&
      runtime.operationGenerationRef.current === generation,
    [runtime]
  );
  const clearRoomJoinedTimeout = useCallback(() => {
    if (roomJoinedTimeoutRef.current) {
      clearTimeout(roomJoinedTimeoutRef.current);
      roomJoinedTimeoutRef.current = null;
    }
  }, []);
  const clearDisconnectedRecoveryTimeout = useCallback(() => {
    if (disconnectedRecoveryTimeoutRef.current) {
      clearTimeout(disconnectedRecoveryTimeoutRef.current);
      disconnectedRecoveryTimeoutRef.current = null;
    }
  }, []);
  const clearRecoveryTimeouts = useCallback(() => {
    clearRoomJoinedTimeout();
    clearDisconnectedRecoveryTimeout();
  }, [clearDisconnectedRecoveryTimeout, clearRoomJoinedTimeout]);
  const scheduleRoomJoinedTimeout = useCallback(
    (
      meeting: MeetingClient,
      joinTarget: TableCallTarget,
      generation: number
    ) => {
      clearRoomJoinedTimeout();
      roomJoinedTimeoutRef.current = setTimeout(() => {
        roomJoinedTimeoutRef.current = null;
        if (
          !isCurrentOperation({
            generation,
            target: joinTarget,
            targetMeeting: meeting,
          }) ||
          meeting.self.roomJoined
        ) {
          return;
        }
        startRuntimeReconnect(runtime, "roomJoined timeout");
        incrementRuntimeOperationGeneration(runtime);
        resetDeviceState();
        setLocalCallPhase(
          "reconnecting",
          runtime.operationGenerationRef.current
        );
        setAudioState("recovering", "Reconnecting table audio...");
        runtime.scheduleTransitionRef.current();
      }, ROOM_JOINED_TIMEOUT_MS);
    },
    [
      clearRoomJoinedTimeout,
      isCurrentOperation,
      resetDeviceState,
      runtime,
      setAudioState,
      setLocalCallPhase,
    ]
  );
  const scheduleDisconnectedRecoveryTimeout = useCallback(
    (
      meeting: MeetingClient,
      joinTarget: TableCallTarget,
      generation: number
    ) => {
      clearDisconnectedRecoveryTimeout();
      disconnectedRecoveryTimeoutRef.current = setTimeout(() => {
        disconnectedRecoveryTimeoutRef.current = null;
        if (
          !isCurrentOperation({
            generation,
            target: joinTarget,
            targetMeeting: meeting,
          })
        ) {
          return;
        }
        startRuntimeReconnect(runtime, "roomLeft:disconnected timeout");
        incrementRuntimeOperationGeneration(runtime);
        resetDeviceState();
        setLocalCallPhase(
          "reconnecting",
          runtime.operationGenerationRef.current
        );
        setAudioState("recovering", "Reconnecting table audio...");
        runtime.scheduleTransitionRef.current();
      }, DISCONNECTED_RECOVERY_TIMEOUT_MS);
    },
    [
      clearDisconnectedRecoveryTimeout,
      isCurrentOperation,
      resetDeviceState,
      runtime,
      setAudioState,
      setLocalCallPhase,
    ]
  );
  const prepareAfterRoomJoined = useCallback(
    async (
      meeting: MeetingClient,
      joinTarget: TableCallTarget,
      generation: number
    ) => {
      try {
        const preparationStartedAt = getNow();
        const publishReady = await prepareJoinedMeeting(
          meeting,
          joinTarget,
          generation
        );
        if (
          !isCurrentOperation({
            generation,
            target: joinTarget,
            targetMeeting: meeting,
          })
        ) {
          return;
        }
        logLifecycle("join audio prepared", {
          durationMs: getElapsedMs(preparationStartedAt),
          generation,
          reason: `publish-ready:${String(publishReady)}`,
          target: joinTarget,
        });
      } catch (error) {
        if (
          !isCurrentOperation({
            generation,
            target: joinTarget,
            targetMeeting: meeting,
          })
        ) {
          return;
        }
        reportLifecycleError("join audio preparation failed", error, {
          generation,
          target: joinTarget,
        });
      }
    },
    [isCurrentOperation, prepareJoinedMeeting]
  );
  const handleRoomLeft = useCallback(
    (
      meeting: MeetingClient,
      state: string,
      joinTarget: TableCallTarget,
      generation: number
    ) => {
      if (runtime.meetingRef.current !== meeting) {
        return;
      }
      const reason = `roomLeft:${state}`;
      if (isTransientRoomLeftState(state)) {
        clearRoomJoinedTimeout();
        setLocalCallPhase(
          "reconnecting",
          runtime.operationGenerationRef.current
        );
        setAudioState("recovering", "Reconnecting table audio...");
        scheduleDisconnectedRecoveryTimeout(meeting, joinTarget, generation);
        return;
      }
      clearRecoveryTimeouts();
      if (RESTARTABLE_ROOM_LEFT_STATES.has(state)) {
        startRuntimeReconnect(runtime, reason);
        incrementRuntimeOperationGeneration(runtime);
        resetDeviceState();
        setLocalCallPhase(
          "reconnecting",
          runtime.operationGenerationRef.current
        );
        setAudioState("recovering", "Reconnecting table audio...");
        runtime.scheduleTransitionRef.current();
        return;
      }
      reportLifecycleError(
        "terminal room exit",
        new Error("RTK room left with terminal state"),
        {
          reason,
          target: runtime.activeTargetRef.current,
        }
      );
      setRuntimeDesiredTarget(runtime, null);
      clearRuntimeReconnect(runtime);
      incrementRuntimeOperationGeneration(runtime);
      resetDeviceState();
      setLocalCallPhase("terminal", runtime.operationGenerationRef.current);
      setAudioState("disconnected");
      runtime.scheduleTransitionRef.current();
    },
    [
      clearRecoveryTimeouts,
      clearRoomJoinedTimeout,
      resetDeviceState,
      runtime,
      scheduleDisconnectedRecoveryTimeout,
      setAudioState,
      setLocalCallPhase,
    ]
  );
  const getRoomLeftPhase = useCallback((state: string): LocalCallPhase => {
    if (
      isTransientRoomLeftState(state) ||
      RESTARTABLE_ROOM_LEFT_STATES.has(state)
    ) {
      return "reconnecting";
    }
    return "terminal";
  }, []);
  const retainRemotesOnRoomLeft = useCallback(
    (state: string) => isTransientRoomLeftState(state),
    []
  );

  const performLeave = useCallback(async () => {
    clearRecoveryTimeouts();
    const leaveStartedAt = getNow();
    const meeting = runtime.meetingRef.current;
    const meetingTarget = runtime.activeTargetRef.current;
    const generation = runtime.operationGenerationRef.current;
    logLifecycle("leave requested", { generation, target: meetingTarget });
    markAudioContextDiagnostic("table-call-leave-requested", {
      generation,
      tableId: meetingTarget?.tableId ?? null,
    });
    incrementRuntimeOperationGeneration(runtime);
    const leaveGeneration = runtime.operationGenerationRef.current;
    setLocalCallPhase("idle", leaveGeneration);
    setRtkMeeting(null, leaveGeneration);
    if (meeting) {
      // Detach the active element before a possibly stalled SDK leave. The
      // captured meeting remains owned by this scope until disposal returns.
      if (runtime.meetingRef.current === meeting) {
        clearMeeting();
        stopDeviceApplication();
      }
      const clientLeaveStartedAt = getNow();
      logLifecycle("leave client started", {
        generation,
        target: meetingTarget,
      });
      const left = await leaveClient(meeting, true);
      if (runtime.operationGenerationRef.current !== leaveGeneration) {
        return;
      }
      logLifecycle(left ? "leave client completed" : "leave client failed", {
        durationMs: getElapsedMs(clientLeaveStartedAt),
        generation,
        target: meetingTarget,
      });
    }
    if (runtime.operationGenerationRef.current !== leaveGeneration) {
      return;
    }
    if (!meeting || runtime.meetingRef.current === meeting) {
      clearMeeting();
      stopDeviceApplication();
    }
    if (runtime.isMountedRef.current) {
      setAudioState("disconnected");
    }
    logLifecycle("leave completed", {
      durationMs: getElapsedMs(leaveStartedAt),
      generation,
      target: meetingTarget,
    });
  }, [
    clearMeeting,
    clearRecoveryTimeouts,
    runtime,
    setAudioState,
    setLocalCallPhase,
    setRtkMeeting,
    stopDeviceApplication,
  ]);

  const performJoin = useCallback(
    // Token-refresh cancellation is part of the serialized join lifecycle.
    // eslint-disable-next-line complexity
    async (joinTarget: TableCallTarget) => {
      const joinStartedAt = getNow();
      const generation = runtime.operationGenerationRef.current;
      let client: MeetingClient | null = null;
      let roomLeftState: string | null = null;
      try {
        logLifecycle("join requested", { generation, target: joinTarget });
        markAudioContextDiagnostic("table-call-join-requested", {
          generation,
          tableId: joinTarget.tableId,
        });
        if (runtime.isMountedRef.current) {
          setLocalCallPhase("joining", generation);
          setAudioState("joining");
        }
        const tokenLookupStartedAt = getNow();
        let rtkMeetingAuthToken = tokenCacheRef.current.get(joinTarget.key);
        const usedCachedToken = Boolean(rtkMeetingAuthToken);
        logLifecycle(
          usedCachedToken ? "join token cache hit" : "join token cache miss",
          {
            durationMs: getElapsedMs(tokenLookupStartedAt),
            generation,
            target: joinTarget,
          }
        );
        if (!rtkMeetingAuthToken) {
          const tokenStartedAt = getNow();
          const { rtkMeetingAuthToken: fetchedToken } =
            await tableCallJoin(joinTarget);
          if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
            logLifecycle("join cancelled after token", {
              generation,
              target: joinTarget,
            });
            return;
          }
          rtkMeetingAuthToken = fetchedToken;
          tokenCacheRef.current.set(joinTarget.key, rtkMeetingAuthToken);
          logLifecycle("join token received", {
            durationMs: getElapsedMs(tokenStartedAt),
            generation,
            target: joinTarget,
          });
        }
        if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
          logLifecycle("join cancelled after token", {
            generation,
            target: joinTarget,
          });
          return;
        }
        const clientInitializationStartedAt = getNow();
        const initializedClient = await initClientRef.current({
          authToken: rtkMeetingAuthToken,
          defaults: { audio: false, video: false },
        });
        if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
          if (initializedClient) {
            await leaveClient(initializedClient, false);
          }
          return;
        }
        if (!initializedClient) {
          throw new Error("Failed to initialize table call client");
        }
        client = initializedClient;
        logLifecycle("join client initialized", {
          durationMs: getElapsedMs(clientInitializationStartedAt),
          generation,
          target: joinTarget,
        });
        assignRuntimeMeeting(runtime, initializedClient, joinTarget);
        setRtkMeeting(initializedClient, generation, {
          getRoomLeftPhase,
          isCurrent: () =>
            isJoinOperationCurrent(runtime, joinTarget, generation),
          onRoomJoined: () => {
            if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
              return;
            }
            clearRecoveryTimeouts();
            markAudioContextDiagnostic("table-call-room-joined", {
              generation,
              tableId: joinTarget.tableId,
            });
            assignMeeting(initializedClient, joinTarget);
            clearRuntimeReconnect(runtime);
            void prepareAfterRoomJoined(
              initializedClient,
              joinTarget,
              generation
            );
          },
          onRoomLeft: (state) => {
            if (isJoinOperationCurrent(runtime, joinTarget, generation)) {
              roomLeftState = state;
              markAudioContextDiagnostic("table-call-room-left", {
                generation,
                state,
                tableId: joinTarget.tableId,
              });
              handleRoomLeft(initializedClient, state, joinTarget, generation);
            }
          },
          retainRemotesOnRoomLeft,
        });
        logLifecycle("join client started", { generation, target: joinTarget });
        const clientJoinStartedAt = getNow();
        try {
          await initializedClient.join();
        } catch (error) {
          if (!usedCachedToken || !isAuthenticationError(error)) {
            throw error;
          }

          if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
            await leaveClient(initializedClient, false);
            return;
          }
          tokenCacheRef.current.delete(joinTarget.key);
          setRtkMeeting(null, generation);
          await leaveClient(initializedClient, false);
          if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
            return;
          }
          clearRuntimeMeeting(runtime);
          client = null;
          const refreshStartedAt = getNow();
          const response = await tableCallJoin(joinTarget);
          if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
            logLifecycle("join cancelled after token refresh", {
              generation,
              target: joinTarget,
            });
            return;
          }
          tokenCacheRef.current.set(
            joinTarget.key,
            response.rtkMeetingAuthToken
          );
          logLifecycle("join token refresh retry", {
            durationMs: getElapsedMs(refreshStartedAt),
            generation,
            target: joinTarget,
          });
          const refreshedClient = await initClientRef.current({
            authToken: response.rtkMeetingAuthToken,
            defaults: { audio: false, video: false },
          });
          if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
            if (refreshedClient) {
              await leaveClient(refreshedClient, false);
            }
            return;
          }
          if (!refreshedClient) {
            throw new Error(
              "Failed to initialize table call client after refresh",
              {
                cause: error,
              }
            );
          }
          client = refreshedClient;
          assignRuntimeMeeting(runtime, refreshedClient, joinTarget);
          setRtkMeeting(refreshedClient, generation, {
            getRoomLeftPhase,
            isCurrent: () =>
              isJoinOperationCurrent(runtime, joinTarget, generation),
            onRoomJoined: () => {
              if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
                return;
              }
              clearRecoveryTimeouts();
              markAudioContextDiagnostic("table-call-room-joined", {
                generation,
                tableId: joinTarget.tableId,
              });
              assignMeeting(refreshedClient, joinTarget);
              clearRuntimeReconnect(runtime);
              void prepareAfterRoomJoined(
                refreshedClient,
                joinTarget,
                generation
              );
            },
            onRoomLeft: (state) => {
              if (isJoinOperationCurrent(runtime, joinTarget, generation)) {
                roomLeftState = state;
                markAudioContextDiagnostic("table-call-room-left", {
                  generation,
                  state,
                  tableId: joinTarget.tableId,
                });
                handleRoomLeft(refreshedClient, state, joinTarget, generation);
              }
            },
            retainRemotesOnRoomLeft,
          });
          await refreshedClient.join();
        }
        logLifecycle("join client completed", {
          durationMs: getElapsedMs(clientJoinStartedAt),
          generation,
          target: joinTarget,
        });
        if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
          logLifecycle("join cancelled after client join", {
            generation,
            target: joinTarget,
          });
          if (client) {
            await leaveClient(client, false);
          }
          return;
        }
        if (roomLeftState) {
          logLifecycle("join interrupted by roomLeft", {
            generation,
            reason: roomLeftState,
            target: joinTarget,
          });
          return true;
        }
        if (!client.self.roomJoined) {
          logLifecycle("join awaiting roomJoined", {
            generation,
            target: joinTarget,
          });
          scheduleRoomJoinedTimeout(client, joinTarget, generation);
          return true;
        }
        logLifecycle("join completed", {
          durationMs: getElapsedMs(joinStartedAt),
          generation,
          target: joinTarget,
        });
      } catch (error) {
        if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
          if (client) {
            await leaveClient(client, false);
          }
          return;
        }
        reportLifecycleError("join failed", error, {
          durationMs: getElapsedMs(joinStartedAt),
          generation,
          target: joinTarget,
        });
        if (client) {
          const cleanupStartedAt = getNow();
          logLifecycle("join cleanup started", {
            generation,
            target: joinTarget,
          });
          await leaveClient(client, false);
          if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
            return;
          }
          setRtkMeeting(null, generation);
          if (runtime.meetingRef.current === client) {
            clearMeeting();
            stopDeviceApplication();
          }
          logLifecycle("join cleanup completed", {
            durationMs: getElapsedMs(cleanupStartedAt),
            generation,
            target: joinTarget,
          });
        }
        if (!isJoinOperationCurrent(runtime, joinTarget, generation)) {
          logLifecycle("join retry cancelled", {
            generation,
            target: joinTarget,
          });
          return;
        }
        logLifecycle("join retry scheduled", {
          generation,
          target: joinTarget,
        });
        runtime.scheduleTransitionRef.current();
        return true;
      }
    },
    [
      assignMeeting,
      clearMeeting,
      clearRecoveryTimeouts,
      getRoomLeftPhase,
      handleRoomLeft,
      prepareAfterRoomJoined,
      retainRemotesOnRoomLeft,
      runtime,
      setAudioState,
      setLocalCallPhase,
      setRtkMeeting,
      scheduleRoomJoinedTimeout,
      stopDeviceApplication,
      tokenCacheRef,
    ]
  );

  const reconcileRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const reconcile = useCallback(() => {
    const reconcileCurrentTarget = async (): Promise<void> => {
      const desired = runtime.desiredTargetRef.current;
      const active = runtime.activeTargetRef.current;
      if (isRuntimeReconnecting(runtime) && runtime.meetingRef.current) {
        await performLeave();
        return reconcileCurrentTarget();
      }
      if ((desired?.key ?? null) === (active?.key ?? null)) {
        if (!desired && runtime.meetingRef.current) {
          await performLeave();
          return reconcileCurrentTarget();
        }
        if (
          !(desired || runtime.meetingRef.current) &&
          runtime.isMountedRef.current &&
          runtime.statusRef.current !== "disconnected"
        ) {
          setLocalCallPhase("idle", runtime.operationGenerationRef.current);
        }
        return;
      }
      if (!desired || active) {
        await performLeave();
        return reconcileCurrentTarget();
      }
      if (await performJoin(desired)) {
        return;
      }
      return reconcileCurrentTarget();
    };
    return reconcileCurrentTarget();
  }, [performJoin, performLeave, runtime, setLocalCallPhase]);
  useEffect(() => {
    reconcileRef.current = reconcile;
  }, [reconcile]);
  const scheduleTransition = useCallback(() => {
    const requestId = isRuntimeReconnecting(runtime)
      ? runtime.reconnectRequestIdRef.current
      : null;
    void enqueueSerialized(runtime.lifecycleQueueRef, async () => {
      try {
        await reconcileRef.current();
      } finally {
        if (requestId === runtime.reconnectRequestIdRef.current) {
          clearRuntimeReconnect(runtime);
        }
      }
    });
  }, [runtime]);
  useEffect(() => {
    setRuntimeScheduleTransition(runtime, scheduleTransition);
  }, [runtime, scheduleTransition]);
  useEffect(() => {
    setRuntimeMounted(runtime, true);
    return () => {
      clearRecoveryTimeouts();
      setRuntimeMounted(runtime, false);
      setRuntimeDesiredTarget(runtime, null);
      incrementRuntimeOperationGeneration(runtime);
      scheduleTransition();
    };
  }, [clearRecoveryTimeouts, runtime, scheduleTransition]);
  useEffect(() => {
    const key = target?.key ?? null;
    if (previousTargetKeyRef.current !== key) {
      logLifecycle("target changed", { target });
      incrementRuntimeOperationGeneration(runtime);
      resetMicrophoneState();
      resetRuntimeRecoveryCounters(runtime);
      clearRecoveryTimeouts();
      previousTargetKeyRef.current = key;
    }
    setRuntimeDesiredTarget(runtime, target);
    scheduleTransition();
  }, [
    clearRecoveryTimeouts,
    resetMicrophoneState,
    runtime,
    scheduleTransition,
    target,
  ]);
  return {
    activeTarget,
    currentMeeting,
    isCurrentOperation,
  };
};
